import type { CSSProperties } from 'react'
import { cn } from '@/lib/cn'
import styles from './ScoreGauge.module.scss'

export interface ScoreGaugeProps {
  /** 0..1 score; null/undefined renders the empty state. */
  score: number | null | undefined
  /** Outer diameter in pixels. The inner number scales proportionally. */
  size?: number
  /** Override the colour ramp (success/ember/danger by default). */
  color?: string
  className?: string
}

/**
 * Score readout used both inline (table rows) and prominent (rail card).
 * Maps the 0..1 score onto a 0..100 conic-gradient sweep, picking a
 * threshold-based color so a score's "good vs bad" is legible at a glance.
 *
 * Inline-styled to avoid emitting one CSS rule per gauge instance.
 */
export function ScoreGauge({ score, size = 46, color, className }: ScoreGaugeProps) {
  const isEmpty = score === null || score === undefined
  const pct = isEmpty ? 0 : Math.max(0, Math.min(1, score)) * 100
  const c = color ?? colorFor(score ?? null)
  const fontSize = Math.round(size * 0.3)

  const style: CSSProperties = {
    width: `${String(size)}px`,
    height: `${String(size)}px`,
    // CSS custom properties typed via index signature.
    ['--p' as string]: pct.toFixed(1),
    ['--c' as string]: c,
  }

  return (
    <div className={cn(styles.gauge, isEmpty && styles.empty, className)} style={style}>
      <span className={styles.value} style={{ fontSize: `${String(fontSize)}px` }}>
        {isEmpty ? '—' : score.toFixed(2)}
      </span>
    </div>
  )
}

function colorFor(score: number | null): string {
  if (score === null) return 'var(--fg-subtle)'
  if (score >= 0.65) return 'var(--success)'
  if (score >= 0.4) return 'var(--brand-ignite)'
  return 'var(--danger)'
}
